export interface LeakCategory {
  slug: string;
  name: string;
  short: string;
  desc: string;
  icon: string;
}

export interface LeakTimelineItem {
  time: string;
  text: string;
}

export interface Leak {
  id: string;
  title: string;
  summary: string;
  category: string;
  tags: string[];
  source: string;
  publishedAt: string;
  readingTime: string;
  hot: number;
  cover: string;
  highlights: string[];
  content: string[];
  timeline: LeakTimelineItem[];
}

export const categories: LeakCategory[] = [
  {
    slug: "ent",
    name: "娱乐现场",
    short: "现场",
    desc: "录制花絮、路透画面、活动直击，把镜头外的细节摊开讲清楚。",
    icon: "ri-mic-line",
  },
  {
    slug: "tech",
    name: "科技内幕",
    short: "内幕",
    desc: "新机曝光、系统迭代、供应链动向，理性拆解每一次技术风向。",
    icon: "ri-cpu-line",
  },
  {
    slug: "web",
    name: "网络热议",
    short: "热议",
    desc: "热搜背后的来龙去脉，梳理全网讨论里真正值得看的那部分。",
    icon: "ri-fire-line",
  },
  {
    slug: "industry",
    name: "行业风向",
    short: "风向",
    desc: "品牌动作、资本动向、渠道变化，用一条线索串起整个行业的走向。",
    icon: "ri-line-chart-line",
  },
  {
    slug: "life",
    name: "生活新鲜事",
    short: "生活",
    desc: "市集、夜游、赛事、新品，记录城市里正在发生的新鲜场面。",
    icon: "ri-leaf-line",
  },
];

export const leaks: Leak[] = [
  {
    id: "front-row-variety-taping",
    title: "综艺录制现场路透流出，舞台调度比正片还精彩",
    summary:
      "有观众在场外拍到节目录制的空档画面：连续三次走位重录、导师席临时换位，现场工作人员跑动频繁。我们把能确认的时间线整理成一条清晰线索。",
    category: "ent",
    tags: ["录制现场", "路透", "花絮"],
    source: "现场观众线索",
    publishedAt: "2026-09-10 09:40",
    readingTime: "4 分钟",
    hot: 9860,
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20lit%20television%20studio%20stage%20with%20empty%20host%20chairs%20and%20soft%20amber%20spotlights%2C%20dark%20curtain%20background%2C%20cinematic%20editorial%20photography%2C%20moody%20atmosphere%2C%20no%20people&width=900&height=580&seq=kelou-ent-stage-01&orientation=landscape",
    highlights: [
      "同一段群舞走位重录三次，疑似为镜头角度让步",
      "导师席临时对调座位，现场大屏内容同步切换",
      "中场补妆时间延长，观众被安排暂时离场",
    ],
    content: [
      "据多位在场观众描述，当天下午的录制节奏明显比常规场次更慢。开场群舞段落完成后，执行导演连续三次要求全组回到起始位，重新走位，现场大屏也在反复切换镜头预览。",
      "有观众注意到，导师席在中场休息后发生了对调：原本靠左的座位换到了右侧，桌面提示卡也一并重印。知情人士称，这类调整多与镜头机位和灯光分区有关，并不代表节目内容有大的改动。",
      "值得一提的是，中场补妆时间被拉长到近二十分钟，观众被引导到侧厅短暂休息。现场工作人员的解释是设备需要重新校准，但并未说明具体原因。",
      "我们联系了节目组的一条公开渠道，暂未得到回应。需要说明的是，以上内容来自现场观众的公开描述与拍摄画面，具体录制安排仍以官方信息为准。",
      "从传播角度看，这类“幕后现场”之所以受欢迎，是因为它把精致的成片拆回到真实的生产流程里。对普通观众而言，看到重录、换位、补妆这些细节，反而更能理解一档节目的制作难度。",
    ],
    timeline: [
      { time: "14:10", text: "观众入场，舞台灯光完成第一次校准" },
      { time: "14:35", text: "开场群舞首次录制，随后连续重录两次" },
      { time: "15:20", text: "导师席对调座位，提示卡同步更换" },
      { time: "16:05", text: "中场补妆延长，观众被引导至侧厅休息" },
      { time: "17:40", text: "当日录制结束，观众分批离场" },
    ],
  },
  {
    id: "ev-road-test-camouflage",
    title: "新车重度伪装路测被拍，尾灯造型和申报图对不上",
    summary:
      "有网友在城市快速路拍到一台重度伪装测试车，尾灯轮廓与早前流传的申报图存在明显差异。从轮胎规格和充电口位置看，这大概率是一台新的纯电平台车型。",
    category: "tech",
    tags: ["新车", "路测", "纯电"],
    source: "网友行车记录线索",
    publishedAt: "2026-09-10 08:15",
    readingTime: "5 分钟",
    hot: 8120,
    cover:
      "https://readdy.ai/api/search-image?query=Heavily%20camouflaged%20prototype%20car%20driving%20on%20a%20city%20expressway%20at%20dusk%2C%20motion%20blur%20background%2C%20dark%20cinematic%20tones%20with%20amber%20street%20lights%2C%20editorial%20automotive%20photography&width=900&height=580&seq=kelou-ev-roadtest-02&orientation=landscape",
    highlights: [
      "尾灯为贯穿式设计，与早前申报图的分体造型不同",
      "充电口位于右后翼子板，位置符合新平台布局",
      "轮胎为低滚阻型号，尺寸接近 19 英寸",
    ],
    content: [
      "拍摄画面的时间约在傍晚六点，车辆以正常车流速度行驶，车顶和前后保险杠均贴有锯齿状伪装贴纸。从侧面轮廓判断，车身比例更接近中大型轿车而非 SUV。",
      "最容易被识别的细节是尾灯。画面中车辆尾部是一条连续的灯带，而早前网络上流传的申报图则显示为分体式布局。这意味着要么申报图对应的并非同一台车，要么产品在定型前做过一次外观调整。",
      "轮胎方面，测试车使用了明显偏向低滚阻的型号，胎壁标识与市面上常见的舒适型轮胎不同。结合右后翼子板处的充电口位置，可以推测该车基于新的纯电平台开发。",
      "需要提醒的是，伪装测试车的用途非常多，可能是底盘验证、热管理测试，也可能是三电标定。单凭路测画面并不能判断量产版本的样子。",
      "对于关注新车的用户来说，路测阶段其实是最有价值的观察窗口：它不会告诉你最终价格，但能透露尺寸、平台和设计方向这些更难临时更改的信息。",
    ],
    timeline: [
      { time: "18:05", text: "网友在快速路匝道拍到伪装测试车" },
      { time: "18:06", text: "车辆并线，尾部贯穿式灯带被清晰记录" },
      { time: "18:09", text: "测试车驶离主路，进入产业园区方向" },
    ],
  },
  {
    id: "folding-phone-leak-teaser",
    title: "折叠屏新机宣传物料外流，铰链形容和上一代完全不同",
    summary:
      "一组疑似发布前的宣传图在社交平台上流传，文案提到“更薄的中间态”。对照上一代的物料口径，这次厂商明显在强调折痕和重量，而不是影像。",
    category: "tech",
    tags: ["折叠屏", "新机", "曝光"],
    source: "社媒物料线索",
    publishedAt: "2026-09-09 21:30",
    readingTime: "4 分钟",
    hot: 7640,
    cover:
      "https://readdy.ai/api/search-image?query=Folded%20slim%20flexible%20smartphone%20lying%20on%20a%20soft%20warm%20beige%20surface%2C%20minimal%20studio%20lighting%2C%20shallow%20depth%20of%20field%2C%20clean%20premium%20product%20photography%2C%20no%20logos&width=900&height=580&seq=kelou-foldphone-03&orientation=landscape",
    highlights: [
      "宣传口径从影像旗舰转向轻薄折痕",
      "铰链结构图示变化明显，中间过渡区域更窄",
      "配色新增一款偏暖的素皮版本",
    ],
    content: [
      "流出的物料共四张，排版风格与上一代发布会前的预热海报高度一致，但关键词从影像换成了手感。其中一张配文写着“更薄的中间态”，直指折叠屏长期被吐槽的厚度问题。",
      "从铰链的结构示意看，新的承托区域比上一代更窄，折叠后的贴合度应该会有所提升。不过结构图只展示原理，不能直接等同于量产效果。",
      "配色上出现了此前没有的暖调素皮版本，质感偏哑光。考虑到上一代主推的是玻璃后盖，这次增加素皮可能是为了在重量和握持感上做区分。",
      "需要说明的是，物料真伪尚无法核实，部分细节也可能是渠道商自行加工。建议关注发布会当天的实际参数，尤其是折叠态厚度和整机重量。",
      "折叠屏走到今天，硬件差距正在收窄，真正拉开体验的往往是折痕处理和软件适配。这也是这次物料把重心放在形态而非影像上的原因。",
    ],
    timeline: [
      { time: "20:50", text: "社交平台出现疑似预热海报，共四张" },
      { time: "21:30", text: "话题进入平台热榜，讨论集中在厚度" },
      { time: "22:10", text: "部分渠道开始下架相关图片" },
    ],
  },
  {
    id: "short-drama-crew-overtime",
    title: "短剧剧组连轴转被曝光，群演晒出通告单讲述真实节奏",
    summary:
      "一位群演把自己的通告单发到社交平台，单日排了七个场景。这份通告单折射出短剧行业快产快销的节奏，也让外界看到这个新兴内容形态的另一面。",
    category: "ent",
    tags: ["短剧", "通告单", "行业观察"],
    source: "剧组成员线索",
    publishedAt: "2026-09-09 19:05",
    readingTime: "5 分钟",
    hot: 6980,
    cover:
      "https://readdy.ai/api/search-image?query=Behind%20the%20scenes%20film%20set%20with%20lighting%20stands%20and%20a%20clapperboard%20on%20a%20table%2C%20warm%20amber%20light%2C%20cozy%20indoor%20scene%2C%20cinematic%20editorial%20photography%2C%20no%20people&width=900&height=580&seq=kelou-shortdrama-04&orientation=landscape",
    highlights: [
      "单日通告覆盖七个场景，最早一场在清晨六点",
      "群演按天结算，超出时长另行计算",
      "剧组采用双机位拍摄，追求一条过的高效率",
    ],
    content: [
      "通告单显示，当天从清晨六点的外景开始，一直排到夜里十一点的棚内收尾。七个场景里，有四个是同一栋别墅的不同房间，靠布景和灯光做区分。",
      "发布者表示，自己作为群演按天结算，如果拍摄超出约定时长，会另行计算。这个模式在短剧行业里比较常见，也和这类内容追求快速上线有关。",
      "从拍摄方式看，剧组采用双机位同时记录，尽量减少重拍。这种打法能压缩周期，但对演员的一次成型能力要求较高，也更容易出现台词与口型的小瑕疵。",
      "行业观察者认为，短剧的爆发本质上是用更短的制作周期去匹配更快的分发节奏。它带来的不只是内容形态的变化，也包括一整套新的用工和结算方式。",
      "对观众来说，了解这些制作端的细节，能帮助更理性地看待短剧的成片质量；对从业者来说，如何在效率与体验之间找到平衡，仍是待解的题。",
    ],
    timeline: [
      { time: "06:00", text: "别墅外景第一场开机" },
      { time: "13:20", text: "转场至棚内，双机位布光耗时约四十分钟" },
      { time: "17:45", text: "群演换装，进入第二个角色的镜头" },
      { time: "23:00", text: "当日通告收尾，剧组清场" },
    ],
  },
  {
    id: "ai-tool-free-tier-change",
    title: "热门 AI 工具改免费额度规则，老用户吐槽用完才发现",
    summary:
      "有用户发现某款热门 AI 工具的免费额度从按天改成了按任务。改动没有明显公告，不少人是在提交任务后才发现次数已经用完。",
    category: "tech",
    tags: ["AI 工具", "额度", "用户体验"],
    source: "用户反馈线索",
    publishedAt: "2026-09-09 15:20",
    readingTime: "4 分钟",
    hot: 6520,
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20desk%20setup%20with%20a%20laptop%20showing%20an%20abstract%20warm%20interface%20and%20a%20cup%20of%20coffee%2C%20soft%20natural%20window%20light%2C%20clean%20beige%20tones%2C%20editorial%20photography&width=900&height=580&seq=kelou-aitool-05&orientation=landscape",
    highlights: [
      "额度单位从每天固定次数变为按任务复杂度的点数制",
      "改动未见醒目公告，帮助中心文档同步更新",
      "部分老用户的剩余额度被折算，折算规则不透明",
    ],
    content: [
      "多位用户反馈，自己在提交一个较长的任务后，当天的免费额度就直接见底了。翻看帮助文档才发现，计费口径已经从每天固定次数变成了按任务复杂度的点数制。",
      "新的规则里，短任务消耗较少的点数，长任务、多轮任务消耗明显更多。对习惯把工具当随手问答的用户来说，这个变化感知不大；但对用它做长文档处理的用户，影响就比较直接。",
      "争议最大的是老用户的历史额度。有人表示自己的剩余次数被自动折算成点数，但折算比例并未在页面上说明，只能通过客服渠道逐一确认。",
      "从产品角度看，按复杂度计费更贴近实际的算力成本，但它对用户的可预期性提出了更高要求。清晰的额度提示和任务前预估，会是这类工具接下来必须补齐的体验。",
      "目前该工具在设置页增加了一个额度面板，可以看到当日剩余点数。是否会把预估消耗前置到提交按钮旁，还有待观察。",
    ],
    timeline: [
      { time: "上午", text: "帮助中心文档更新，计费口径改为点数制" },
      { time: "午后", text: "陆续有用户反馈额度消耗异常" },
      { time: "15:20", text: "设置页上线额度面板，展示当日剩余" },
    ],
  },
  {
    id: "livestream-price-war",
    title: "直播带货价格战再起，多个直播间同一款商品报价相差近百元",
    summary:
      "同一款日常消费品在不同直播间的到手价出现了明显差异。我们比对了几场直播的截图，差异主要来自赠品组合，而不是标价本身。",
    category: "industry",
    tags: ["直播", "价格", "消费观察"],
    source: "消费者比对线索",
    publishedAt: "2026-09-08 22:10",
    readingTime: "4 分钟",
    hot: 5980,
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20lit%20home%20studio%20desk%20with%20a%20ring%20light%20and%20a%20smartphone%20on%20a%20tripod%2C%20cozy%20amber%20ambience%2C%20blurred%20background%2C%20editorial%20photography%2C%20no%20people&width=900&height=580&seq=kelou-livestream-06&orientation=landscape",
    highlights: [
      "标价基本一致，差异集中在赠品与满减门槛",
      "部分直播间把赠品折算成等值金额，拉大视觉价差",
      "跨平台比价需要看到手价，而非页面标价",
    ],
    content: [
      "比对的三场直播里，这款商品的页面标价完全相同，但到手价最多相差九十多元。原因并不复杂：不同直播间搭配的赠品数量不一样，有的送两支替换装，有的只送一支。",
      "更需要注意的是等值折算。有直播间在话术里把赠品按官方售价折成金额，直接叠加在优惠上，视觉效果上差价被进一步放大。但这部分价值能否兑现，取决于用户是否真的用得上。",
      "满减门槛也是变量之一。有的直播间要求凑单到指定金额才享受最低价，实际结算时如果缺少凑单商品，最终价格会回落到更高的档位。",
      "对消费者来说，最稳妥的做法是把各直播间的到手价单独列出来，再比较赠品清单。把页面标价直接横向对比，很容易得出错误结论。",
      "从行业角度看，这种围绕赠品做差异的打法，说明标价空间已经比较透明，平台和主播的竞争正在往组合权益转移。",
    ],
    timeline: [
      { time: "20:00", text: "A 直播间开播，页面标价公布" },
      { time: "20:40", text: "B 直播间同款上架，赠品数量不同" },
      { time: "22:10", text: "比对完成，到手价差异趋于清晰" },
    ],
  },
  {
    id: "night-market-reopen",
    title: "老夜市改造后回归，摊主说摊位费没涨但规矩多了",
    summary:
      "闭市改造三个月的夜市重新开张，动线、灯光和排烟系统都做了调整。摊主们最直接的感受，是经营更规范了，但出摊节奏也被重新安排。",
    category: "life",
    tags: ["夜市", "城市生活", "烟火气"],
    source: "摊主与顾客线索",
    publishedAt: "2026-09-08 19:30",
    readingTime: "4 分钟",
    hot: 5430,
    cover:
      "https://readdy.ai/api/search-image?query=Bustling%20street%20food%20night%20market%20with%20warm%20hanging%20lights%20and%20steaming%20stalls%2C%20cozy%20amber%20glow%2C%20evening%20atmosphere%2C%20editorial%20travel%20photography%2C%20no%20readable%20text&width=900&height=580&seq=kelou-nightmarket-07&orientation=landscape",
    highlights: [
      "重新规划了单向动线，主通道拓宽约一米",
      "统一排烟与垃圾收集点，摊主需要限时清运",
      "摊位费维持不变，但增加了经营时段管理",
    ],
    content: [
      "改造后的夜市最直观的变化是动线。原本容易被堵住的十字路口被改成单向循环，主通道拓宽了大约一米，推婴儿车和轮椅的空间也宽松了不少。",
      "摊主们提到最多的是排烟和垃圾。过去各家自行处理，收摊后地面常留油渍；现在统一设置了排烟管接口和垃圾收集点，摊主需要在规定时间内清运，否则会被提醒。",
      "费用方面，摊位费和改造前基本持平，这让不少老摊主松了口气。不过经营时段被重新划分，热门位置需要轮换，摊主之间也因此有了新的协商机制。",
      "对常来的顾客来说，体验提升是明显的：找摊位更容易，油烟更少，地面也干净了。也有老顾客怀念过去那种挤一挤的热闹，觉得现在整齐了，但也少了点随意。",
      "夜市之所以牵动情绪，是因为它同时承载了生计与生活。如何在规范和烟火气之间找到平衡，可能比任何一次硬件改造都更难。",
    ],
    timeline: [
      { time: "18:00", text: "夜市重新开张，单向动线启用" },
      { time: "18:30", text: "主通道人流高峰，通行明显顺畅" },
      { time: "21:00", text: "统一垃圾收集点首次集中清运" },
    ],
  },
  {
    id: "food-brand-collab-leak",
    title: "老牌食品联名口味提前流出，包装配色引发两极讨论",
    summary:
      "一张疑似联名包装的实拍图在网上扩散，口味是此前没尝试过的组合。讨论焦点却意外地集中在配色上，支持与反对的声音几乎一样多。",
    category: "industry",
    tags: ["联名", "包装", "新品"],
    source: "渠道实拍线索",
    publishedAt: "2026-09-08 14:40",
    readingTime: "3 分钟",
    hot: 5120,
    cover:
      "https://readdy.ai/api/search-image?query=Colorful%20snack%20packaging%20mockup%20flat%20lay%20on%20a%20warm%20cream%20background%2C%20soft%20studio%20shadow%2C%20minimal%20product%20photography%2C%20no%20readable%20brand%20text&width=900&height=580&seq=kelou-collabfood-08&orientation=landscape",
    highlights: [
      "联名口味为两款此前未上市的组合",
      "包装主色偏向低饱和，与品牌常规风格反差明显",
      "部分门店已经开始铺货，官方预热尚未启动",
    ],
    content: [
      "从实拍图看，联名包装采用了低饱和的主色，整体风格比品牌以往的产品年轻不少。正是因为反差太大，讨论很快从口味转到了设计本身。",
      "口味方面，泄露的信息显示是两款新的组合，一款偏果香，一款偏焦糖。这两者都不属于品牌此前的常规产品线，算是一次比较大胆的尝试。",
      "有渠道人员表示，部分门店已经收到货并开始上架，但官方的预热物料还没发布。这种渠道先于官方的情况在食品行业并不少见，通常是因为铺货需要提前。",
      "包装争议其实反映了一个老话题：消费者对老品牌既期待创新，又希望它保持熟悉的调性。如何在两者之间取舍，往往决定了联名是加分还是翻车。",
      "需要提醒的是，目前流传的图片无法完全确认真伪，最终口味与包装以官方发布为准。",
    ],
    timeline: [
      { time: "上午", text: "渠道开始收货，实拍图在小范围流传" },
      { time: "14:40", text: "图片扩散至社交平台，配色成讨论焦点" },
      { time: "傍晚", text: "部分门店完成上架，位置相对靠前" },
    ],
  },
  {
    id: "film-roadshow-crowd",
    title: "电影路演临时加场，影院经理讲述观众比主创还投入",
    summary:
      "原定两场的路演因为观众反响热烈临时增加一场。影院方面透露，加场的决定几乎是在开场前两小时才定下来的。",
    category: "ent",
    tags: ["电影", "路演", "影院"],
    source: "影院现场线索",
    publishedAt: "2026-09-07 21:15",
    readingTime: "4 分钟",
    hot: 4860,
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20lit%20movie%20theater%20auditorium%20with%20empty%20red%20seats%20and%20a%20glowing%20screen%2C%20cinematic%20amber%20lighting%2C%20moody%20editorial%20photography%2C%20no%20people&width=900&height=580&seq=kelou-filmroadshow-09&orientation=landscape",
    highlights: [
      "加场决定在开场前两小时敲定，主创调整了行程",
      "观众提问远超预设时长，主持人多次协调时间",
      "散场后仍有观众在影厅外等候交流",
    ],
    content: [
      "影院经理回忆，那天下午的票在开售后不久就售罄，后台的候补人数还在涨。经过和片方沟通，最终在开场前两小时确定了加场安排。",
      "临时加场意味着主创的行程要重新协调。据现场工作人员透露，几位主创取消了原定的休息时间，直接从上一场赶过来，中间几乎没有停顿。",
      "提问环节明显超时。原本预留的十五分钟被拉长到近四十分钟，主持人多次出面协调。观众的问题集中在创作动机和某个情节的处理上，讨论相当具体。",
      "散场后，仍有不少观众在影厅外等候，希望和主创交流。影院临时延长了场地的开放时间，直到最后一位观众离场。",
      "对一部作品来说，路演的价值不只在宣传。它让创作者直接听到观众最真实的反馈，这种反馈往往比任何数据都更具体。",
    ],
    timeline: [
      { time: "16:00", text: "当天下午票售罄，候补人数持续上升" },
      { time: "17:30", text: "确定加场，主创调整行程" },
      { time: "19:30", text: "加场路演开始，提问环节大幅超时" },
      { time: "22:10", text: "最后一批观众离场，场地延长开放" },
    ],
  },
  {
    id: "mobile-game-version-survey",
    title: "手游新版本玩法提前出现在测试问卷里，老玩家意见分歧明显",
    summary:
      "一份流出的玩家问卷显示，新版本可能引入一套偏协作的团队机制。问卷还没结束，社区里已经吵成两派：一派觉得该改，一派怕改动太大。",
    category: "web",
    tags: ["手游", "版本", "玩家社区"],
    source: "玩家问卷线索",
    publishedAt: "2026-09-07 18:00",
    readingTime: "4 分钟",
    hot: 4610,
    cover:
      "https://readdy.ai/api/search-image?query=Game%20controller%20and%20smartphone%20on%20a%20dark%20wooden%20desk%20with%20warm%20ambient%20light%2C%20moody%20cinematic%20still%20life%2C%20shallow%20depth%20of%20field%2C%20no%20readable%20text&width=900&height=580&seq=kelou-mobilegame-10&orientation=landscape",
    highlights: [
      "问卷中出现团队协作副本、角色分工等新选项",
      "老玩家担心单人体验被削弱",
      "官方尚未确认，问卷被标注为概念调研",
    ],
    content: [
      "流出的问卷里，出现了几个此前版本没有出现过的词，比如团队协作副本和角色分工。问卷把这些选项放在一起，询问玩家对组队门槛和奖励分配的看法。",
      "支持的一方认为，游戏目前的社交深度不足，长期玩下来容易变成单机体验，引入协作机制能让老玩家重新有目标。",
      "反对的一方则担心，协作玩法会抬高参与门槛，尤其是对习惯碎片时间上线的玩家不友好。他们更希望新版本先优化单人内容的产出节奏。",
      "需要强调的是，问卷本身被标注为概念调研，官方并没有承诺任何玩法落地。概念调研的作用，恰恰是筛选出哪些想法值得继续做下去。",
      "对一款运营多年的游戏来说，任何一次大的机制调整都是平衡木。玩家的分歧，本质上是不同游戏习惯之间的差异，很难有一刀切的答案。",
    ],
    timeline: [
      { time: "下午", text: "玩家问卷在小范围发放，含新玩法选项" },
      { time: "18:00", text: "问卷内容流出，社区开始讨论" },
      { time: "晚间", text: "社区形成支持与反对两派观点" },
    ],
  },
  {
    id: "city-marathon-route-change",
    title: "城市马拉松路线微调曝光，跑者最关心的是补给点位置",
    summary:
      "赛事路线图在报名页面更新，终点区域做出调整，几个补给点的位置也随之变化。对跑者而言，这比风景好不好看重要得多。",
    category: "life",
    tags: ["马拉松", "赛事", "城市"],
    source: "跑者社群线索",
    publishedAt: "2026-09-07 11:20",
    readingTime: "3 分钟",
    hot: 3980,
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20city%20street%20with%20marathon%20route%20barriers%20and%20banners%20at%20sunrise%2C%20warm%20golden%20light%2C%20clean%20urban%20photography%2C%20no%20people%2C%20no%20readable%20text&width=900&height=580&seq=kelou-marathon-11&orientation=landscape",
    highlights: [
      "终点区域从广场北侧调整到南侧，疏散口增多",
      "三处补给点位置微调，间隔更均匀",
      "半程分流点前移，缓解拥堵",
    ],
    content: [
      "更新后的路线图显示，终点区域从广场北侧调整到了南侧。这个变化看起来不大，但周边的地铁口和公交站点分布不同，直接影响完赛后的疏散效率。",
      "跑者最关心的补给点也做了微调。三处补给点的位置发生了变化，整体间隔比赛前更均匀。此前有跑者反馈，后半程某一公里内补给点过于集中。",
      "半程分流点比往年提前了一段距离。组委会的思路应该是把两股人流的交叉提前，避免在狭窄路段形成拥堵。",
      "对多数跑者来说，路线风景是加分项，补给和分流才是决定体验的核心。能否在关键位置提供足够的饮水和医疗支持，比经过哪些地标更重要。",
      "目前官方仅更新了路线图，具体的补给清单和医疗点分布尚未公布，跑者社群仍在等待更详细的说明。",
    ],
    timeline: [
      { time: "上午", text: "报名页面更新路线图，终点区域调整" },
      { time: "11:20", text: "跑者社群发现补给点位置变化" },
      { time: "午后", text: "讨论集中在半程分流点的调整" },
    ],
  },
  {
    id: "music-festival-lineup-rumor",
    title: "音乐节阵容传言满天飞，主办方用一张剪影图回应",
    summary:
      "在阵容正式公布前，各种版本的内部名单已经在群里传了好几轮。主办方没有正面回应，而是发了一张剪影图，让讨论更热闹了。",
    category: "ent",
    tags: ["音乐节", "阵容", "演出"],
    source: "乐迷社群线索",
    publishedAt: "2026-09-06 20:30",
    readingTime: "3 分钟",
    hot: 3720,
    cover:
      "https://readdy.ai/api/search-image?query=Festival%20stage%20at%20night%20with%20warm%20amber%20lighting%20and%20silhouetted%20equipment%2C%20crowd%20blurred%20in%20the%20foreground%2C%20cinematic%20concert%20photography%2C%20no%20readable%20text&width=900&height=580&seq=kelou-festival-12&orientation=landscape",
    highlights: [
      "多个版本的内幕名单在社群里流传",
      "主办方发布剪影图，未做文字说明",
      "乐迷根据剪影轮廓展开猜测，热度持续走高",
    ],
    content: [
      "距离开票还有一段时间，关于阵容的讨论已经提前升温。社群里流传着至少三个版本的名字，彼此之间差异不小，但都没有可核实的来源。",
      "主办方没有选择直接辟谣，而是在官方账号发布了一张剪影图。图片里能看到几个模糊的轮廓，配文只有一个表情符号，等于把猜测空间留给了乐迷。",
      "这种方式在演出行业里越来越常见。与其被动回应传言，不如主动给出一个可讨论的视觉线索，既维持热度，又不至于承诺具体名字。",
      "对乐迷来说，猜阵容本身也是音乐节体验的一部分。但需要提醒的是，任何未被官方确认的名单都只能当参考，购票前最好以官方渠道为准。",
      "从传播效果看，这张剪影图确实达到了目的。相关讨论的互动量明显上升，不少人开始逐帧对比轮廓细节。",
    ],
    timeline: [
      { time: "午后", text: "社群流传第一版疑似阵容名单" },
      { time: "20:30", text: "主办方发布剪影图，未配文字说明" },
      { time: "深夜", text: "乐迷开始逐帧对比剪影轮廓" },
    ],
  },
];